import { SamFilter, SamFilterSource, SamFilterState, SamFilterType, samQuickFilter } from "../filterTypes";

export interface SetFiltersAction<T extends SamFilter> {
  filters: Omit<T, "source">[];
  source: SamFilterSource;
  type: SamFilterType;
}

export interface AddFilterAction<T extends SamFilter> {
  filter: Omit<T, "action" | "source">;
  source: SamFilterSource;
  type: SamFilterType;
}

export interface RemoveFiltersAction {
  source: SamFilterSource;
  type: SamFilterType;
}

export interface SetSearchFiltersAction {
  source: SamFilterSource;
  searchString: string;
}

export const withFilterState = <T extends SamFilter>(filterState: SamFilterState<T>) => ({
  setFilters({ type, source, filters }: SetFiltersAction<T>) {
    filterState[type] = filters.map((filter) => ({ ...filter, source }) as T);
  },
  setSearchFilters({ source, searchString }: SetSearchFiltersAction) {
    filterState[SamFilterType.RAW_SEARCH] = createRawSearchFilters(source, searchString);
    filterState[SamFilterType.SEARCH] = createSearchFilters(source, searchString);
  },
  addExcludeFilter({ type, source, filter }: AddFilterAction<T>) {
    const filters = getOrCreate(filterState, type, createFilters);
    filters.push({ ...filter, action: "EXCLUDE", source });
  },
  addIncludeFilter({ type, source, filter }: AddFilterAction<T>) {
    const filters = getOrCreate(filterState, type, createFilters);
    filters.push({ ...filter, action: "INCLUDE", source });
  },
  setFiltersForSource({ type, source, filters }: SetFiltersAction<T>) {
    const oldFilters = getOrCreate(filterState, type, createFilters);
    const newFilters = oldFilters.filter((it) => it.source !== source);
    newFilters.push(...filters.map((filter) => ({ ...filter, source })));
    filterState[type] = newFilters as T[];
  },
  removeFiltersForSource({ type, source }: RemoveFiltersAction) {
    const filters = getOrCreate(filterState, type, createFilters);
    filterState[type] = filters.filter((it) => it.source !== source) as T[];
  },
  invertFilters(type: SamFilterType) {
    Object.values(filterState[type] ?? {})
      .flatMap((filters) => filters)
      .forEach((filter) => invertFilterAction(filter));
  },
  clearFilters(type: SamFilterType) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete filterState[type];
  },
  clearAllFilters() {
    Object.values(SamFilterType).forEach((filterType) => this.clearFilters(filterType));
  },
});

// creates a placeholder filter, containing the raw search string (and operator "contains")
const createRawSearchFilters = <T extends SamFilter>(source: string, searchString: string): T[] => {
  return [{
    action: "INCLUDE",
    field: samQuickFilter,
    operator: "contains",
    value: searchString,
    source,
  } as T];
};

const createSearchFilters = <T extends SamFilter>(source: string, searchString: string): T[] => {
  const searchPattern = searchString
    .replace(/[|\\{}()[\]^$+*?.]/g, "\\$&")
    .split(/(?<!\\)\s/).filter(Boolean)
    .join("|");

  return [{
    action: "INCLUDE",
    field: samQuickFilter,
    operator: "regex_match",
    value: [searchPattern, "i"],
    source,
  } as T];
};

const invertFilterAction = (filter: SamFilter) => {
  filter.action = filter.action !== "EXCLUDE"
    ? "EXCLUDE"
    : "INCLUDE";
};

const createFilters = <T extends SamFilter>(): T[] => [];
const getOrCreate = <K extends PropertyKey, V>(obj: Partial<Record<K, V>>, key: K, creator: () => V): V => {
  const value = obj[key] ?? creator();
  obj[key] = value;
  return value;
};
